/**
 * GanttView — composite container that wires the new Gantt with all controls.
 * Receives tasks + sync metadata as props and manages columns/period/zoom state.
 * Used by:
 *   - ProjectDetail (single-sync default)
 *   - Multi-project mode (when user picks via MultiProjectSelector)
 *   - SharedView (public)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import GanttChart, { buildDistinctValues as _bdv, filterByCollapse as _fbc, getValue, parseDate } from "./GanttChart";
import GanttToolbar from "./GanttToolbar";
import ColumnsPanel from "./ColumnsPanel";
import TaskDetailModal from "./TaskDetailModal";
import ExportMenu from "./ExportMenu";
import MultiProjectSelector from "./MultiProjectSelector";
import SavedViewsMenu from "./SavedViewsMenu";
import CompareSelector from "./CompareSelector";
import { http } from "../lib/api";
import { useI18n } from "../context/I18nContext";

// DEFAULT_COLUMNS uses `labelKey` instead of `label`. We resolve via t()
// at the moment the user-state is merged with the available catalog (see
// the `wiredColumns` memo below). This way language changes apply
// instantly without re-mounting the chart.
const DEFAULT_COLUMNS = [
  { key: "task_id",          labelKey: "col_id",             type: "string",  visible: true,  width: 60 },
  { key: "name",             labelKey: "col_task",           type: "string",  visible: true,  width: 280 },
  { key: "start",            labelKey: "col_start",          type: "date",    visible: true,  width: 90 },
  { key: "finish",           labelKey: "col_finish",         type: "date",    visible: true,  width: 90 },
  { key: "duration_days",    labelKey: "col_duration_days",  type: "number",  visible: true,  width: 70 },
  { key: "percent_complete", labelKey: "col_percent",        type: "percent", visible: true,  width: 60 },
  { key: "resource_names",   labelKey: "col_resources",      type: "string",  visible: false, width: 160 },
  { key: "is_critical",      labelKey: "col_is_critical",    type: "boolean", visible: false, width: 70 },
];

const TASK_SEARCHABLE_KEYS = new Set(["name", "task_id", "wbs"]);

// Map of backend `available_columns` keys → i18n label keys. The backend
// always returns labels in PT-BR (its data dictionary is bilingual-ignorant
// by design); we translate on the client when the user picks EN. Keys not
// in this table keep their backend label as-is (custom_fields.* and any
// future fields).
const CATALOG_LABEL_KEYS = {
  task_id: "col_id",
  name: "col_task",
  start: "col_start",
  finish: "col_finish",
  duration_days: "col_duration_days",
  percent_complete: "col_percent",
  resource_names: "col_resources",
  resources: "col_resources",
  is_critical: "col_is_critical",
  wbs: "col_wbs",
  outline_level: "col_outline_level",
  is_summary: "col_is_summary",
  milestone: "col_milestone",
  constraint_type: "col_constraint_type",
  constraint_date: "col_constraint_date",
  actual_start: "col_actual_start",
  actual_finish: "col_actual_finish",
  actual_duration: "col_actual_duration",
  baseline_start: "col_baseline_start",
  baseline_finish: "col_baseline_finish",
  baseline_duration: "col_baseline_duration",
  percent_work_complete: "col_work_complete",
  cost: "col_cost",
  actual_cost: "col_actual_cost",
  free_slack: "col_free_slack",
  total_slack: "col_total_slack",
  early_start: "col_early_start",
  early_finish: "col_early_finish",
  late_start: "col_late_start",
  late_finish: "col_late_finish",
  priority: "col_priority",
  active: "col_active",
};

export default function GanttView({
  initialSelection = [],
  initialTasks = [],
  syncMeta = {},
  projects = {},
  availableColumnsCatalog = [],
  projectName = "Cornerstone",
  embeddable = false,
  canEditCompany = false,
  isPublic = false,
  /** Token of the active share link — enables read-only saved-views fetch
   *  from /api/share/{token}/saved-views when set. */
  shareToken = null,
}) {
  const { t } = useI18n();
  // selection state (for multi-project mode)
  const [selection, setSelection] = useState(initialSelection);

  // remote tasks (when selection changes)
  const [tasks, setTasks] = useState(initialTasks);
  const [loading, setLoading] = useState(false);

  // controls
  const [zoom, setZoom] = useState("fit");
  const [period, setPeriod] = useState(null);
  const [criticalOnly, setCriticalOnly] = useState(false);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);

  // Layout prefs (persisted via Saved Views)
  const [hSplit, setHSplit] = useState(null);            // null = auto from column widths
  const [collapsedUids, setCollapsedUids] = useState([]); // string[] for serialisability

  // panels
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [multiOpen, setMultiOpen] = useState(false);
  const [viewsOpen, setViewsOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [detailTask, setDetailTask] = useState(null);

  // compare
  const [compareRef, setCompareRef] = useState(null); // { project_id, sync_id }
  const [compareTasks, setCompareTasks] = useState(null);
  // Active diff filter (one of "added"|"removed"|"delayed"|"accelerated"|null)
  const [diffFilter, setDiffFilter] = useState(null);

  const containerRef = useRef(null);
  // Snapshot of the *initial* selection/tasks/isPublic flag. We only consult
  // these to short-circuit the fetch when the parent has already passed
  // tasks for the same selection. Keeping them in a ref means the fetch
  // effect can stay scoped to `selection` without ESLint dep warnings.
  const initialsRef = useRef({ initialSelection, initialTasks, isPublic });
  initialsRef.current = { initialSelection, initialTasks, isPublic };

  // Sync columns visibility when external catalog grows
  useEffect(() => {
    if (!availableColumnsCatalog?.length) return;
    setColumns((prev) => {
      // remove cols that are not in catalog (custom_fields drift)
      const keep = prev.filter((c) => availableColumnsCatalog.find((x) => x.key === c.key) || ["task_id","name","start","finish","duration_days","percent_complete","resource_names","is_critical"].includes(c.key));
      return keep;
    });
  }, [availableColumnsCatalog]);

  // Fetch tasks when selection changes (multi mode).
  //
  // PUBLIC MODE: do NOT call /tasks/query (no auth). SharedView pre-filters
  // the task list client-side and passes it via `initialTasks` whenever the
  // PublicProjectPicker selection changes; we just mirror that into local
  // state via the `initialTasks` watcher in the next effect.
  useEffect(() => {
    if (!selection || selection.length === 0) return;
    const { initialSelection: initSel, initialTasks: initTasks, isPublic: pub } = initialsRef.current;
    if (pub) return;
    // If parent gave us initialTasks AND selection matches, skip fetch (single mode)
    if (initTasks?.length && selection.length === initSel.length) {
      const same = selection.every((s, i) => s.sync_id === initSel[i]?.sync_id);
      if (same) { setTasks(initTasks); return; }
    }
    (async () => {
      setLoading(true);
      try {
        const r = await http.post("/tasks/query", { selection });
        const items = (r.data.tasks || []).map((t) => ({ ...t, sync_id: t.sync_id, __color: t.__color }));
        setTasks(items);
      } catch (e) { toast.error(e.response?.data?.detail || e.message); }
      finally { setLoading(false); }
    })();
  }, [selection]);

  // In public mode, the parent (`SharedView`) computes filtered tasks every
  // time the user picks from the PublicProjectPicker. Keep our local state
  // in sync without making an authenticated API call.
  useEffect(() => {
    if (!isPublic) return;
    setTasks(initialTasks);
  }, [initialTasks, isPublic]);

  // Fetch compare tasks
  useEffect(() => {
    if (!compareRef?.sync_id) { setCompareTasks(null); setDiffFilter(null); return; }
    (async () => {
      try {
        const r = await http.post("/tasks/query", { selection: [{ project_id: compareRef.project_id, sync_id: compareRef.sync_id }] });
        setCompareTasks(r.data.tasks || []);
      } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    })();
  }, [compareRef]);

  // Compute task-level diff between the current `tasks` (target) and the
  // `compareTasks` (base) so the user can see WHICH activities slipped /
  // accelerated and click to drill into them. We keep the indexing local
  // to avoid an extra API round-trip — the compare server endpoint is
  // still used by the dedicated Sync Compare tab.
  const diff = useMemo(() => {
    if (!compareTasks || !tasks?.length) return null;
    const byUidTarget = new Map(tasks.map((t) => [String(t.task_uid), t]));
    const byUidBase = new Map(compareTasks.map((t) => [String(t.task_uid), t]));
    const added = [];     // in target, NOT in base
    const removed = [];   // in base, NOT in target
    const delayed = [];   // finish moved LATER
    const accelerated = []; // finish moved EARLIER
    for (const [uid, t] of byUidTarget) {
      const b = byUidBase.get(uid);
      if (!b) { added.push(uid); continue; }
      if (!t.finish || !b.finish) continue;
      if (t.finish > b.finish) delayed.push(uid);
      else if (t.finish < b.finish) accelerated.push(uid);
    }
    for (const [uid] of byUidBase) {
      if (!byUidTarget.has(uid)) removed.push(uid);
    }
    return { added, removed, delayed, accelerated };
  }, [tasks, compareTasks]);

  // When the user clicks a diff chip, narrow the visible task set to just
  // those task_uids. We do this by injecting a synthetic "diff filter"
  // column on top of normal filters in the chart's `tasks` prop.
  const diffFilteredTasks = useMemo(() => {
    if (!diffFilter || !diff) return tasks;
    const allowed = new Set(diff[diffFilter] || []);
    if (allowed.size === 0) return [];
    // Keep ancestors visible too so the hierarchy still makes sense.
    const byUid = new Map(tasks.map((t) => [String(t.task_uid), t]));
    const expanded = new Set(allowed);
    for (const uid of allowed) {
      let p = byUid.get(uid)?.parent_uid;
      while (p && !expanded.has(String(p))) {
        expanded.add(String(p));
        p = byUid.get(String(p))?.parent_uid;
      }
    }
    return tasks.filter((t) => expanded.has(String(t.task_uid)));
  }, [tasks, diff, diffFilter]);

  // Filter column setters: wire onFilterChange to update local state. Also
  // resolve `labelKey` → translated `label` so the headers always reflect
  // the current UI language. Columns added from the catalog already carry
  // a `label` string (server-localised) and we leave those untouched.
  const wiredColumns = useMemo(() => columns.map((c) => ({
    ...c,
    label: c.labelKey ? t(c.labelKey) : c.label,
    onFilterChange: (key, val) => setColumns((prev) => prev.map((x) => x.key === key ? { ...x, filter: val } : x)),
  })), [columns, t]);

  // Persist column-width changes (drag-to-resize from the header)
  const handleColumnWidthChange = (key, w) => {
    setColumns((prev) => prev.map((x) => x.key === key ? { ...x, width: w } : x));
  };

  // Compute the EXACT set of tasks that the user sees after filters —
  // ExportMenu needs this so the PNG matches what's on screen.
  const exportTasks = useMemo(() => {
    let list = tasks.map((t) => ({ ...t, _start: parseDate(t.start), _finish: parseDate(t.finish) }));
    if (criticalOnly) list = list.filter((t) => t.is_critical);
    if (period?.start || period?.end) {
      const ps = period.start ? new Date(period.start + "T00:00:00") : null;
      const pe = period.end ? new Date(period.end + "T23:59:59") : null;
      list = list.filter((t) => {
        const ts = t._start, tf = t._finish;
        if (!ts && !tf) return true;
        const s = ts || tf, f = tf || ts;
        if (ps && f < ps) return false;
        if (pe && s > pe) return false;
        return true;
      });
    }
    const activeFilters = columns.filter((c) => c.visible && (c.filter || "").toString().trim());
    if (activeFilters.length) {
      list = list.filter((t) => activeFilters.every((c) => {
        const v = getValue(t, c.key);
        if (v == null) return false;
        const f = c.filter.toString().trim().toLowerCase();
        if (TASK_SEARCHABLE_KEYS.has(c.key)) return String(v).toLowerCase().includes(f);
        if (Array.isArray(v)) return v.some((x) => String(x).toLowerCase() === f);
        return String(v).toLowerCase() === f;
      }));
    }
    return list;
  }, [tasks, criticalOnly, period, columns]);

  const onAddFromCatalog = (col) => {
    setColumns((prev) => [...prev, { key: col.key, label: col.label, type: col.type, visible: true, width: 120 }]);
  };

  const applySavedView = (state) => {
    if (!state) return;
    if (state.zoom) setZoom(state.zoom);
    if (state.period !== undefined) setPeriod(state.period);
    if (state.criticalOnly !== undefined) setCriticalOnly(state.criticalOnly);
    if (state.columns) setColumns(state.columns);
    if (state.selection) setSelection(state.selection);
    if (state.compareRef !== undefined) setCompareRef(state.compareRef);
    if (state.hSplit !== undefined) setHSplit(state.hSplit);
    if (state.collapsedUids !== undefined) setCollapsedUids(state.collapsedUids || []);
  };

  const currentState = {
    zoom, period, criticalOnly,
    columns: columns.map((c) => ({ ...c, onFilterChange: undefined })),
    selection, compareRef,
    hSplit, collapsedUids,
  };

  return (
    <div className="space-y-3" data-testid="gantt-view">
      <GanttToolbar
        zoom={zoom} onZoomChange={setZoom}
        period={period} onPeriodChange={setPeriod}
        criticalOnly={criticalOnly} onCriticalOnlyChange={setCriticalOnly}
        onOpenColumns={() => setColumnsOpen(true)}
        onOpenViews={() => setViewsOpen(true)}
        onOpenExport={() => setExportOpen(true)}
        onOpenMultiProject={() => setMultiOpen(true)}
        onOpenCompare={() => setCompareOpen(true)}
        isPublic={isPublic}
        onClearFilters={() => {
          setPeriod(null);
          setCriticalOnly(false);
          setColumns((prev) => prev.map((c) => ({ ...c, filter: "" })));
        }}
        hasColumnFilters={columns.some((c) => c.visible && (c.filter || "").toString().trim())}
        compareSyncId={compareRef?.sync_id}
        taskCount={tasks.length}
        totalTasks={tasks.length}
      />

      {diff && (
        <div className="bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="gantt-diff-bar">
          <span className="cs-label !text-[10px] mr-1">{t("diff_vs_base")}</span>
          <DiffChip label={t("diff_added")} count={diff.added.length} tone="green"
            active={diffFilter === "added"} onClick={() => setDiffFilter(diffFilter === "added" ? null : "added")} testid="diff-chip-added" />
          <DiffChip label={t("diff_removed")} count={diff.removed.length} tone="red"
            active={diffFilter === "removed"} onClick={() => setDiffFilter(diffFilter === "removed" ? null : "removed")} testid="diff-chip-removed" />
          <DiffChip label={t("diff_delayed")} count={diff.delayed.length} tone="red"
            active={diffFilter === "delayed"} onClick={() => setDiffFilter(diffFilter === "delayed" ? null : "delayed")} testid="diff-chip-delayed" />
          <DiffChip label={t("diff_accelerated")} count={diff.accelerated.length} tone="green"
            active={diffFilter === "accelerated"} onClick={() => setDiffFilter(diffFilter === "accelerated" ? null : "accelerated")} testid="diff-chip-accelerated" />
          {diffFilter && (
            <button onClick={() => setDiffFilter(null)} className="ml-auto text-[11px] font-mono text-zinc-500 hover:text-cs-accent" data-testid="diff-clear">
              {t("diff_clear")}
            </button>
          )}
        </div>
      )}

      <div ref={containerRef}>
        <GanttChart
          tasks={diffFilter ? diffFilteredTasks : tasks}
          columns={wiredColumns}
          period={period}
          zoom={zoom}
          showCriticalOnly={criticalOnly}
          compareTasks={compareTasks}
          syncMeta={syncMeta}
          onTaskOpen={setDetailTask}
          onColumnWidthChange={handleColumnWidthChange}
          hSplit={hSplit}
          onHSplitChange={setHSplit}
          collapsedUids={collapsedUids}
          onCollapsedChange={setCollapsedUids}
          emptyText={loading ? t("loading") : t("empty_no_tasks_selection")}
          containerHeight={Math.max(400, (typeof window !== "undefined" ? window.innerHeight : 800) - 360)}
        />
      </div>

      <ColumnsPanel
        open={columnsOpen}
        onClose={() => setColumnsOpen(false)}
        columns={wiredColumns}
        onChange={setColumns}
        // Localise the "Available to add" catalog: backend ships PT labels
        // but our i18n covers every standard schema key. Custom fields stay
        // untouched (they may carry user-defined aliases from the .mpp file).
        availableCatalog={availableColumnsCatalog.map((c) => ({
          ...c,
          label: CATALOG_LABEL_KEYS[c.key] ? t(CATALOG_LABEL_KEYS[c.key]) : c.label,
        }))}
        onAddFromCatalog={onAddFromCatalog}
      />
      <ExportMenu
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        tasks={exportTasks}
        columns={wiredColumns}
        syncMeta={syncMeta}
        projectName={projectName}
      />
      <MultiProjectSelector open={multiOpen} onClose={() => setMultiOpen(false)} value={selection} onChange={setSelection} />
      {!isPublic && <SavedViewsMenu open={viewsOpen} onClose={() => setViewsOpen(false)} screen="gantt" currentState={currentState} onApply={applySavedView} canSaveCompany={canEditCompany} />}
      {isPublic && shareToken && <SavedViewsMenu open={viewsOpen} onClose={() => setViewsOpen(false)} screen="gantt" onApply={applySavedView} readOnly shareToken={shareToken} />}
      <CompareSelector open={compareOpen} onClose={() => setCompareOpen(false)} value={compareRef} onChange={setCompareRef} />
      <TaskDetailModal task={detailTask} onClose={() => setDetailTask(null)} />
    </div>
  );
}

/**
 * Pill button shown in the compare-diff bar. Clicking filters the Gantt
 * to ONLY tasks in that diff bucket (plus their ancestors so the
 * hierarchy still renders). Clicking the same chip again clears.
 */
function DiffChip({ label, count, tone, active, onClick, testid }) {
  const base = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono border transition-colors";
  const tones = {
    green: active ? "bg-cs-success text-white border-cs-success" : "bg-white border-green-200 text-green-800 hover:bg-green-50",
    red:   active ? "bg-cs-critical text-white border-cs-critical" : "bg-white border-red-200 text-red-800 hover:bg-red-50",
  };
  return (
    <button onClick={onClick} className={`${base} ${tones[tone]}`} data-testid={testid} disabled={count === 0}>
      <span className="font-semibold">{count}</span>
      <span>{label}</span>
    </button>
  );
}
